import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import API from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Settings, Users, MessageSquare, FlaskConical, CreditCard, Loader2, Save, Key, Info, BarChart3, TrendingUp, PieChart, Download, ShieldAlert, Clock, Mail, Activity, Search, AlertTriangle, Euro, Pause, Play, Trash2, Coins, Zap, Wallet, Tag, FileText } from "lucide-react";
import { LLM_PROVIDERS } from "@/data/staticData";
import { toast } from "sonner";
import { Navigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, LineChart, Line, PieChart as RePieChart, Pie, Cell } from "recharts";

const CUSTOM_PROVIDERS = ["mistral", "deepseek", "grok", "google"]; // require a per-provider API key (NOT openrouter)
const PIE_COLORS = ["#64748B", "#F59E0B", "#7C3AED"];
const SECTOR_COLORS = ["#0B1B3D", "#2563EB", "#10B981", "#F59E0B", "#DC2626", "#7C3AED", "#EC4899", "#06B6D4"];
const ACTIVITY_ICONS = { inscription: Users, chat: MessageSquare, paiement: CreditCard };
const ACTIVITY_COLORS = { inscription: "text-blue-500", chat: "text-green-500", paiement: "text-purple-500" };

const PROVIDER_COLORS = { anthropic: "#D97706", openai: "#10B981", gemini: "#2563EB", google: "#2563EB", mistral: "#7C3AED", deepseek: "#EC4899", grok: "#0EA5E9" };
const FEATURE_COLORS = { chat: "#0B1B3D", simulations: "#2563EB", diagnostics: "#10B981", quiz: "#F59E0B" };

function formatNum(n) {
  if (n === null || n === undefined) return "—";
  if (n >= 1_000_000) return (n / 1_000_000).toFixed(1) + "M";
  if (n >= 1_000) return (n / 1_000).toFixed(1) + "k";
  return Number(n).toLocaleString("fr-FR");
}

function formatEur(n) {
  if (n === null || n === undefined) return "—";
  if (n < 0.01) return n.toFixed(4) + " €";
  return n.toFixed(2) + " €";
}

function formatDate(iso) {
  if (!iso) return "—";
  try { const d = new Date(iso); return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" }) + " " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }); }
  catch { return iso.slice(0, 16).replace("T", " "); }
}

export default function AdminPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [analytics, setAnalytics] = useState(null);
  const [extended, setExtended] = useState(null);
  const [llmConfig, setLlmConfig] = useState(null);
  const [tierConfigs, setTierConfigs] = useState({
    free: { selectedLLM: "", apiKey: "", saving: false },
    premium: { selectedLLM: "", apiKey: "", saving: false },
  });
  const [loading, setLoading] = useState(true);
  const [userFilter, setUserFilter] = useState({ role: "all", search: "" });
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [suspendTarget, setSuspendTarget] = useState(null);
  const [usageData, setUsageData] = useState(null);
  const [usagePeriod, setUsagePeriod] = useState(30);
  const [usageLoading, setUsageLoading] = useState(false);
  const [coupons, setCoupons] = useState([]);
  const [newCoupon, setNewCoupon] = useState({ code: "", discount_type: "percent", discount_value: 10, valid_until: "", max_uses: "" });
  const [savingCoupon, setSavingCoupon] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [orModels, setOrModels] = useState([]);
  const [orModelsLoading, setOrModelsLoading] = useState(false);
  const [orModelsFetchedAt, setOrModelsFetchedAt] = useState(null);
  const [orSearch, setOrSearch] = useState("");
  const isAdmin = user?.role === "admin";
  const currentUserId = user?._id || user?.id;

  useEffect(() => { if (isAdmin) loadData(); }, [isAdmin]);
  useEffect(() => { if (isAdmin) loadUsage(usagePeriod); }, [isAdmin, usagePeriod]);
  useEffect(() => { if (isAdmin) { loadCoupons(); loadAuditLogs(); loadOrModels(); } }, [isAdmin]);

  const loadOrModels = async (refresh = false) => {
    setOrModelsLoading(true);
    try {
      const { data } = await API.get(`/admin/openrouter-models${refresh ? "?refresh=true" : ""}`);
      setOrModels(data.models || []);
      setOrModelsFetchedAt(data.fetched_at);
    } catch { /* silent */ }
    finally { setOrModelsLoading(false); }
  };

  if (!isAdmin) return <Navigate to="/dashboard" replace />;

  const loadData = async () => {
    setLoading(true);
    try {
      const [usersRes, statsRes, llmRes, analyticsRes, extRes] = await Promise.all([
        API.get("/admin/users"), API.get("/admin/stats"), API.get("/admin/llm-config"),
        API.get("/admin/analytics"), API.get("/admin/analytics/extended")
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setLlmConfig(llmRes.data);
      setAnalytics(analyticsRes.data);
      setExtended(extRes.data);
      // Initialize per-tier selection from response (backend returns {free, premium, ...})
      const freeCfg = llmRes.data.free || llmRes.data;
      const premCfg = llmRes.data.premium || llmRes.data;
      setTierConfigs({
        free: { selectedLLM: `${freeCfg.provider}:${freeCfg.model}`, apiKey: "", saving: false },
        premium: { selectedLLM: `${premCfg.provider}:${premCfg.model}`, apiKey: "", saving: false },
      });
    } catch { toast.error("Erreur lors du chargement"); }
    finally { setLoading(false); }
  };

  const loadUsage = async (days) => {
    setUsageLoading(true);
    try {
      const { data } = await API.get(`/admin/usage-detailed?days=${days}`);
      setUsageData(data);
    } catch { toast.error("Erreur chargement usage tokens"); }
    finally { setUsageLoading(false); }
  };

  const loadCoupons = async () => {
    try { const { data } = await API.get("/admin/coupons"); setCoupons(data); } catch {}
  };
  const loadAuditLogs = async () => {
    try { const { data } = await API.get("/admin/audit-logs?limit=100"); setAuditLogs(data.logs); } catch {}
  };
  const createCoupon = async () => {
    if (!newCoupon.code.trim() || !newCoupon.discount_value) { toast.error("Code et valeur requis"); return; }
    setSavingCoupon(true);
    try {
      const payload = { ...newCoupon, discount_value: Number(newCoupon.discount_value) };
      if (payload.max_uses === "") delete payload.max_uses; else payload.max_uses = Number(payload.max_uses);
      if (!payload.valid_until) delete payload.valid_until; else payload.valid_until = new Date(payload.valid_until).toISOString();
      payload.active = true;
      await API.post("/admin/coupons", payload);
      toast.success(`Coupon ${newCoupon.code.toUpperCase()} créé`);
      setNewCoupon({ code: "", discount_type: "percent", discount_value: 10, valid_until: "", max_uses: "" });
      loadCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
    finally { setSavingCoupon(false); }
  };
  const toggleCoupon = async (code) => {
    try { await API.put(`/admin/coupons/${code}/toggle`); loadCoupons(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };
  const deleteCoupon = async (code) => {
    if (!window.confirm(`Supprimer le coupon ${code} ?`)) return;
    try { await API.delete(`/admin/coupons/${code}`); toast.success("Supprimé"); loadCoupons(); }
    catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const updateRole = async (userId, role) => {
    try {
      await API.put(`/admin/users/${userId}/role`, { role });
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, role } : u));
      toast.success("Rôle mis à jour");
    } catch { toast.error("Erreur"); }
  };

  const toggleSuspend = async (userId) => {
    try {
      const { data } = await API.put(`/admin/users/${userId}/suspend`);
      setUsers(prev => prev.map(u => u._id === userId ? { ...u, suspended: data.suspended } : u));
      setSuspendTarget(null);
      toast.success(data.message);
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const deleteUser = async (userId) => {
    try {
      await API.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u._id !== userId));
      setDeleteTarget(null);
      toast.success("Utilisateur supprimé");
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur"); }
  };

  const exportCSV = () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/export-csv`;
    window.open(url, "_blank");
  };

  const saveLLMConfig = async (tier) => {
    const tc = tierConfigs[tier];
    if (!tc) return;
    const [provider, model] = tc.selectedLLM.split(":");
    const isCustom = CUSTOM_PROVIDERS.includes(provider);
    const tierCfg = (llmConfig && llmConfig[tier]) || {};
    if (isCustom && !tc.apiKey.trim() && !tierCfg.has_api_key) {
      toast.error("Clé API requise pour ce provider."); return;
    }
    setTierConfigs(p => ({ ...p, [tier]: { ...p[tier], saving: true } }));
    try {
      const payload = { provider, model, tier };
      if (tc.apiKey.trim()) payload.api_key = tc.apiKey.trim();
      else if (!isCustom) payload.api_key = null;
      await API.post("/admin/llm-config", payload);
      toast.success(`Configuration ${tier === "free" ? "Gratuit" : "Premium"} mise à jour`);
      loadData();
    } catch { toast.error("Erreur"); }
    finally {
      setTierConfigs(p => ({ ...p, [tier]: { ...p[tier], saving: false } }));
    }
  };

  // Filtres utilisateurs
  const filteredUsers = users.filter(u => {
    if (userFilter.role !== "all" && u.role !== userFilter.role) return false;
    if (userFilter.search) {
      const q = userFilter.search.toLowerCase();
      return (u.name || "").toLowerCase().includes(q) || (u.email || "").toLowerCase().includes(q);
    }
    return true;
  });

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="h-6 w-6 animate-spin text-[var(--ia-navy)]" /></div>;
  const emergentProviders = LLM_PROVIDERS.filter(p => p.group === "openrouter" || p.group === "emergent");
  const customProviders = LLM_PROVIDERS.filter(p => p.group === "custom");

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="admin-page">
      <div className="mb-6 animate-fade-in">
        <div className="flex items-center gap-3 mb-2">
          <Settings className="h-6 w-6 text-[var(--ia-navy)]" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Administration</h1>
        </div>
      </div>

      {/* KPIs enrichis */}
      {stats && extended && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 animate-fade-in">
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <Users className="h-4 w-4 mx-auto mb-1 text-[var(--ia-blue)]" />
            <p className="text-xl font-bold">{stats.total_users}</p><p className="text-[10px] text-[var(--ia-muted)]">Utilisateurs</p>
          </CardContent></Card>
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <Activity className="h-4 w-4 mx-auto mb-1 text-green-500" />
            <p className="text-xl font-bold">{extended.active_today}</p><p className="text-[10px] text-[var(--ia-muted)]">Actifs aujourd'hui</p>
          </CardContent></Card>
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <TrendingUp className="h-4 w-4 mx-auto mb-1 text-amber-500" />
            <p className="text-xl font-bold">{extended.retention_7d}%</p><p className="text-[10px] text-[var(--ia-muted)]">Rétention 7j</p>
          </CardContent></Card>
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <TrendingUp className="h-4 w-4 mx-auto mb-1 text-purple-500" />
            <p className="text-xl font-bold">{extended.conversion_rate}%</p><p className="text-[10px] text-[var(--ia-muted)]">Conversion Premium</p>
          </CardContent></Card>
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <Euro className="h-4 w-4 mx-auto mb-1 text-emerald-500" />
            <p className="text-xl font-bold">{extended.monthly_revenue}€</p><p className="text-[10px] text-[var(--ia-muted)]">Revenu mensuel</p>
          </CardContent></Card>
          <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
            <Mail className="h-4 w-4 mx-auto mb-1 text-blue-400" />
            <p className="text-xl font-bold">{extended.newsletter_active}</p><p className="text-[10px] text-[var(--ia-muted)]">Newsletter</p>
          </CardContent></Card>
        </div>
      )}

      <Tabs defaultValue="analytics" className="animate-fade-in">
        <TabsList className="mb-6 flex-wrap">
          <TabsTrigger value="analytics"><BarChart3 className="h-3.5 w-3.5 mr-1.5" />Analytiques</TabsTrigger>
          <TabsTrigger value="activity"><Activity className="h-3.5 w-3.5 mr-1.5" />Activité</TabsTrigger>
          <TabsTrigger value="users"><Users className="h-3.5 w-3.5 mr-1.5" />Utilisateurs</TabsTrigger>
          <TabsTrigger value="tokens" data-testid="admin-tab-tokens"><Coins className="h-3.5 w-3.5 mr-1.5" />Tokens & Coûts</TabsTrigger>
          <TabsTrigger value="coupons" data-testid="admin-tab-coupons"><Tag className="h-3.5 w-3.5 mr-1.5" />Coupons</TabsTrigger>
          <TabsTrigger value="audit" data-testid="admin-tab-audit"><FileText className="h-3.5 w-3.5 mr-1.5" />Audit</TabsTrigger>
          <TabsTrigger value="security"><ShieldAlert className="h-3.5 w-3.5 mr-1.5" />Sécurité</TabsTrigger>
          <TabsTrigger value="llm"><Settings className="h-3.5 w-3.5 mr-1.5" />Config IA</TabsTrigger>
        </TabsList>

        {/* ─── Analytiques ─── */}
        <TabsContent value="analytics">
          {analytics && (
            <div className="space-y-6">
              <Card className="border-[var(--ia-border)]">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}><TrendingUp className="h-4 w-4 text-[var(--ia-navy)]" /> Utilisation — 7 derniers jours</CardTitle></CardHeader>
                <CardContent><div className="h-56"><ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analytics.daily_usage} barGap={2}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} /><YAxis tick={{ fontSize: 11, fill: "#64748B" }} />
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                    <Bar dataKey="chat" name="Chat" fill="#0B1B3D" radius={[3, 3, 0, 0]} /><Bar dataKey="simulations" name="Simulations" fill="#2563EB" radius={[3, 3, 0, 0]} />
                    <Bar dataKey="diagnostics" name="Diagnostics" fill="#10B981" radius={[3, 3, 0, 0]} /><Bar dataKey="quiz" name="Quiz" fill="#F59E0B" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer></div></CardContent>
              </Card>
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <Card className="border-[var(--ia-border)]">
                  <CardHeader className="pb-2"><CardTitle className="text-sm"><Users className="h-4 w-4 inline mr-1 text-[var(--ia-blue)]" />Inscriptions 7j</CardTitle></CardHeader>
                  <CardContent><div className="h-40"><ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analytics.registrations}><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" /><XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} /><YAxis tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} /><Line type="monotone" dataKey="inscriptions" stroke="#2563EB" strokeWidth={2} dot={{ r: 3, fill: "#2563EB" }} /></LineChart>
                  </ResponsiveContainer></div></CardContent>
                </Card>
                <Card className="border-[var(--ia-border)]">
                  <CardHeader className="pb-2"><CardTitle className="text-sm"><PieChart className="h-4 w-4 inline mr-1 text-amber-500" />Répartition rôles</CardTitle></CardHeader>
                  <CardContent><div className="h-40"><ResponsiveContainer width="100%" height="100%">
                    <RePieChart><Pie data={analytics.roles.filter(r => r.value > 0)} cx="50%" cy="50%" innerRadius={35} outerRadius={60} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name} (${value})`}>
                    {analytics.roles.filter(r => r.value > 0).map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}</Pie>
                    <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} /></RePieChart>
                  </ResponsiveContainer></div></CardContent>
                </Card>
                {analytics.sectors.length > 0 && (
                  <Card className="border-[var(--ia-border)]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm">Secteurs</CardTitle></CardHeader>
                    <CardContent><div className="h-40"><ResponsiveContainer width="100%" height="100%">
                      <BarChart data={analytics.sectors} layout="vertical"><CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                      <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} allowDecimals={false} /><YAxis dataKey="name" type="category" tick={{ fontSize: 10, fill: "#64748B" }} width={70} />
                      <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                      <Bar dataKey="value" name="Users" radius={[0, 3, 3, 0]}>{analytics.sectors.map((_, i) => <Cell key={i} fill={SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}</Bar>
                      </BarChart></ResponsiveContainer></div></CardContent>
                  </Card>
                )}
              </div>
              {/* Top questions */}
              {extended?.top_questions?.length > 0 && (
                <Card className="border-[var(--ia-border)]">
                  <CardHeader className="pb-2"><CardTitle className="text-sm"><MessageSquare className="h-4 w-4 inline mr-1 text-green-500" />Top questions IA posées</CardTitle></CardHeader>
                  <CardContent><div className="space-y-2">
                    {extended.top_questions.map((q, i) => (
                      <div key={i} className="flex items-center gap-3 text-sm">
                        <span className="w-5 h-5 rounded-full bg-[var(--ia-navy)] text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">{i+1}</span>
                        <span className="flex-1 text-slate-700 truncate">{q.question}</span>
                        <Badge variant="outline" className="text-[10px]">{q.count}x</Badge>
                      </div>
                    ))}
                  </div></CardContent>
                </Card>
              )}
            </div>
          )}
        </TabsContent>

        {/* ─── Activité récente ─── */}
        <TabsContent value="activity">
          {extended && (
            <Card className="border-[var(--ia-border)]">
              <CardHeader><CardTitle className="text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Activité récente</CardTitle></CardHeader>
              <CardContent>
                {extended.activity.length === 0 ? <p className="text-sm text-[var(--ia-muted)]">Aucune activité récente.</p> : (
                  <div className="space-y-3">
                    {extended.activity.map((a, i) => {
                      const Icon = ACTIVITY_ICONS[a.type] || Activity;
                      const color = ACTIVITY_COLORS[a.type] || "text-slate-400";
                      return (
                        <div key={i} className="flex items-start gap-3 text-sm">
                          <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${color}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-slate-700 truncate">{a.label}</p>
                            <p className="text-[10px] text-[var(--ia-muted)]">{formatDate(a.time)}</p>
                          </div>
                          <Badge variant="outline" className="text-[9px] flex-shrink-0">{a.type}</Badge>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        {/* ─── Utilisateurs ─── */}
        <TabsContent value="users">
          <Card className="border-[var(--ia-border)]">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-3">
                <CardTitle className="text-base" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Utilisateurs ({filteredUsers.length}/{users.length})</CardTitle>
                <Button variant="outline" size="sm" onClick={exportCSV} data-testid="export-csv-btn">
                  <Download className="h-3.5 w-3.5 mr-1.5" />Export CSV
                </Button>
              </div>
              {/* Filtres */}
              <div className="flex items-center gap-3 mt-3 flex-wrap">
                <div className="relative flex-1 min-w-[180px]">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[var(--ia-muted)]" />
                  <Input placeholder="Rechercher nom ou email..." className="pl-8 h-8 text-xs" value={userFilter.search} onChange={(e) => setUserFilter(p => ({...p, search: e.target.value}))} data-testid="user-filter-search" />
                </div>
                <Select value={userFilter.role} onValueChange={(v) => setUserFilter(p => ({...p, role: v}))}>
                  <SelectTrigger className="h-8 w-32 text-xs" data-testid="user-filter-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Tous les rôles</SelectItem>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="premium">Premium</SelectItem>
                    <SelectItem value="admin">Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-[10px]">Nom</TableHead>
                    <TableHead className="text-[10px]">Email</TableHead>
                    <TableHead className="text-[10px]">Rôle</TableHead>
                    <TableHead className="text-[10px]">Statut</TableHead>
                    <TableHead className="text-[10px]">Tokens (mois)</TableHead>
                    <TableHead className="text-[10px]">Tokens (total)</TableHead>
                    <TableHead className="text-[10px]">Coût total</TableHead>
                    <TableHead className="text-[10px]">Conv.</TableHead>
                    <TableHead className="text-[10px]">Inscrit le</TableHead>
                    <TableHead className="text-[10px]">Dernière connexion</TableHead>
                    <TableHead className="text-[10px]">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map(u => {
                    const isSelf = u._id === currentUserId;
                    return (
                    <TableRow key={u._id} className={u.suspended ? "bg-red-50/50" : ""} data-testid={`user-row-${u._id}`}>
                      <TableCell className="text-xs">{u.name || "—"}{isSelf && <Badge variant="outline" className="ml-2 text-[9px] bg-blue-50 text-blue-600 border-blue-200">Vous</Badge>}</TableCell>
                      <TableCell className="text-xs">{u.email}</TableCell>
                      <TableCell>
                        <Select value={u.role} onValueChange={(v) => updateRole(u._id, v)} disabled={isSelf}>
                          <SelectTrigger className="h-7 text-[10px] w-24"><SelectValue /></SelectTrigger>
                          <SelectContent><SelectItem value="free">Free</SelectItem><SelectItem value="premium">Premium</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        {u.suspended
                          ? <Badge variant="outline" className="text-[9px] bg-red-50 text-red-600 border-red-200">Suspendu</Badge>
                          : <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">Actif</Badge>
                        }
                      </TableCell>
                      <TableCell className="text-[10px] font-mono">{formatNum(u.tokens_month || 0)}</TableCell>
                      <TableCell className="text-[10px] font-mono">{formatNum(u.tokens_total || 0)}</TableCell>
                      <TableCell className="text-[10px] font-mono text-emerald-700">{formatEur(u.cost_total_eur || 0)}</TableCell>
                      <TableCell className="text-[10px] text-center">{u.conversations_count || 0}</TableCell>
                      <TableCell className="text-[10px] text-[var(--ia-muted)]">{formatDate(u.created_at)}</TableCell>
                      <TableCell className="text-[10px] text-[var(--ia-muted)]">{formatDate(u.last_login_at)}</TableCell>
                      <TableCell>
                        {u.role !== "admin" && !isSelf && (
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setSuspendTarget(u)} data-testid={`suspend-${u._id}`} title={u.suspended ? "Réactiver" : "Suspendre"}>
                              {u.suspended ? <Play className="h-3.5 w-3.5 text-emerald-600" /> : <Pause className="h-3.5 w-3.5 text-amber-600" />}
                            </Button>
                            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDeleteTarget(u)} data-testid={`delete-user-${u._id}`} title="Supprimer">
                              <Trash2 className="h-3.5 w-3.5 text-red-500" />
                            </Button>
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  );})}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Tokens & Coûts ─── */}
        <TabsContent value="tokens" data-testid="admin-tokens-content">
          <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div className="flex items-center gap-2">
                <Coins className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Consommation tokens & coûts IA</h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-[var(--ia-muted)]">Période :</span>
                <Select value={String(usagePeriod)} onValueChange={(v) => setUsagePeriod(Number(v))}>
                  <SelectTrigger className="h-8 w-28 text-xs" data-testid="usage-period-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="7">7 jours</SelectItem>
                    <SelectItem value="30">30 jours</SelectItem>
                    <SelectItem value="90">90 jours</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {usageLoading && <div className="flex items-center justify-center py-8"><Loader2 className="h-5 w-5 animate-spin text-[var(--ia-navy)]" /></div>}

            {!usageLoading && usageData && (
              <>
                {/* Free tier — Month-to-date prominent banner */}
                {usageData.mtd_free && (
                  <Card className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="free-mtd-banner">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                            <Wallet className="h-5 w-5 text-emerald-700" />
                          </div>
                          <div>
                            <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-semibold">Coût Free tier — ce mois</p>
                            <p className="text-2xl font-bold text-emerald-900 font-mono" data-testid="mtd-free-cost">
                              {formatEur(usageData.mtd_free.cost_eur)}
                            </p>
                            <p className="text-[11px] text-emerald-700">
                              {formatNum(usageData.mtd_free.tokens)} tokens · {usageData.mtd_free.calls} appels · {usageData.mtd_free.active_users} utilisateurs actifs
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-[10px] uppercase tracking-wide text-slate-500 font-semibold">Projection fin de mois</p>
                          <p className="text-xl font-bold text-slate-700 font-mono" data-testid="mtd-free-projection">
                            ~ {formatEur(usageData.mtd_free.projected_eom_eur)}
                          </p>
                          <p className="text-[10px] text-slate-500">
                            Jour {usageData.mtd_free.days_elapsed} / {usageData.mtd_free.days_in_month}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* KPIs */}
                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="tokens-kpis">
                  <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
                    <Zap className="h-4 w-4 mx-auto mb-1 text-amber-500" />
                    <p className="text-xl font-bold">{formatNum(usageData.kpis.total_tokens)}</p>
                    <p className="text-[10px] text-[var(--ia-muted)]">Tokens totaux</p>
                  </CardContent></Card>
                  <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
                    <Activity className="h-4 w-4 mx-auto mb-1 text-blue-500" />
                    <p className="text-xl font-bold">{formatNum(usageData.kpis.today_tokens)}</p>
                    <p className="text-[10px] text-[var(--ia-muted)]">Tokens aujourd'hui</p>
                  </CardContent></Card>
                  <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
                    <Wallet className="h-4 w-4 mx-auto mb-1 text-emerald-500" />
                    <p className="text-xl font-bold">{formatEur(usageData.kpis.total_cost_eur)}</p>
                    <p className="text-[10px] text-[var(--ia-muted)]">Coût estimé</p>
                  </CardContent></Card>
                  <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
                    <MessageSquare className="h-4 w-4 mx-auto mb-1 text-purple-500" />
                    <p className="text-xl font-bold">{formatNum(usageData.kpis.total_calls)}</p>
                    <p className="text-[10px] text-[var(--ia-muted)]">Appels IA</p>
                  </CardContent></Card>
                  <Card className="border-[var(--ia-border)]"><CardContent className="p-3 text-center">
                    <Users className="h-4 w-4 mx-auto mb-1 text-slate-500" />
                    <p className="text-xl font-bold">{formatNum(usageData.kpis.avg_tokens_per_user)}</p>
                    <p className="text-[10px] text-[var(--ia-muted)]">Moy. / utilisateur</p>
                  </CardContent></Card>
                </div>

                {/* Daily usage chart */}
                <Card className="border-[var(--ia-border)]">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><TrendingUp className="h-4 w-4 text-[var(--ia-navy)]" />Tokens consommés par jour ({usagePeriod}j)</CardTitle></CardHeader>
                  <CardContent>
                    <div className="h-64"><ResponsiveContainer width="100%" height="100%">
                      <BarChart data={usageData.daily} barGap={2}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#64748B" }} interval={Math.max(0, Math.floor(usageData.daily.length / 12))} />
                        <YAxis tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={formatNum} />
                        <Tooltip contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} formatter={(v, n) => [formatNum(v), n === "input" ? "Entrée" : "Sortie"]} />
                        <Bar dataKey="input" name="Entrée" stackId="a" fill="#2563EB" radius={[0, 0, 0, 0]} />
                        <Bar dataKey="output" name="Sortie" stackId="a" fill="#10B981" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer></div>
                  </CardContent>
                </Card>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Per provider */}
                  <Card className="border-[var(--ia-border)]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><PieChart className="h-4 w-4 text-purple-500" />Tokens par provider</CardTitle></CardHeader>
                    <CardContent>
                      {usageData.providers.length === 0
                        ? <p className="text-xs text-[var(--ia-muted)] py-8 text-center">Aucune donnée sur cette période</p>
                        : <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                            <RePieChart>
                              <Pie data={usageData.providers} dataKey="tokens" nameKey="name" cx="50%" cy="50%" outerRadius={70} label={(e) => `${e.name} ${(e.percent * 100).toFixed(0)}%`}>
                                {usageData.providers.map((p, i) => <Cell key={i} fill={PROVIDER_COLORS[p.name] || SECTOR_COLORS[i % SECTOR_COLORS.length]} />)}
                              </Pie>
                              <Tooltip formatter={(v, _n, props) => [`${formatNum(v)} tokens — ${formatEur(props.payload.cost)}`, props.payload.name]} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                            </RePieChart>
                          </ResponsiveContainer></div>
                      }
                    </CardContent>
                  </Card>

                  {/* Per feature */}
                  <Card className="border-[var(--ia-border)]">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[var(--ia-blue)]" />Tokens par fonctionnalité</CardTitle></CardHeader>
                    <CardContent>
                      {usageData.features.length === 0
                        ? <p className="text-xs text-[var(--ia-muted)] py-8 text-center">Aucune donnée sur cette période</p>
                        : <div className="h-56"><ResponsiveContainer width="100%" height="100%">
                            <BarChart data={usageData.features} layout="vertical">
                              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                              <XAxis type="number" tick={{ fontSize: 10, fill: "#64748B" }} tickFormatter={formatNum} />
                              <YAxis dataKey="name" type="category" tick={{ fontSize: 11, fill: "#64748B" }} width={80} />
                              <Tooltip formatter={(v, _n, props) => [`${formatNum(v)} tokens — ${formatEur(props.payload.cost)} — ${props.payload.calls} appels`, props.payload.name]} contentStyle={{ borderRadius: 6, border: "1px solid #E2E8F0", fontSize: 12 }} />
                              <Bar dataKey="tokens" radius={[0, 3, 3, 0]}>
                                {usageData.features.map((f, i) => <Cell key={i} fill={FEATURE_COLORS[f.name] || "#64748B"} />)}
                              </Bar>
                            </BarChart>
                          </ResponsiveContainer></div>
                      }
                    </CardContent>
                  </Card>
                </div>

                {/* Tier breakdown (Free vs Premium) */}
                {usageData.tiers && (
                  <Card className="border-[var(--ia-border)]" data-testid="tiers-breakdown">
                    <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Wallet className="h-4 w-4 text-emerald-500" />Consommation par tier (Gratuit vs Premium)</CardTitle></CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {usageData.tiers.map(t => (
                          <div key={t.name} className={`p-4 rounded-lg border-2 ${t.name === "premium" ? "border-amber-200 bg-amber-50/30" : "border-emerald-200 bg-emerald-50/30"}`} data-testid={`tier-card-${t.name}`}>
                            <div className="flex items-center justify-between mb-3">
                              <Badge variant="outline" className={t.name === "premium" ? "bg-amber-100 text-amber-700 border-amber-300" : "bg-emerald-100 text-emerald-700 border-emerald-300"}>
                                {t.name === "premium" ? "Premium" : "Gratuit"}
                              </Badge>
                              {t.fallbacks > 0 && <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">{t.fallbacks} fallback{t.fallbacks > 1 ? "s" : ""}</Badge>}
                            </div>
                            <div className="grid grid-cols-2 gap-3 text-sm">
                              <div>
                                <p className="text-[10px] text-[var(--ia-muted)] uppercase">Tokens</p>
                                <p className="text-xl font-bold font-mono">{formatNum(t.tokens)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-[var(--ia-muted)] uppercase">Coût</p>
                                <p className="text-xl font-bold font-mono text-emerald-700">{formatEur(t.cost)}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-[var(--ia-muted)] uppercase">Appels</p>
                                <p className="text-base font-semibold">{t.calls}</p>
                              </div>
                              <div>
                                <p className="text-[10px] text-[var(--ia-muted)] uppercase">Coût / appel</p>
                                <p className="text-base font-semibold">{t.calls > 0 ? formatEur(t.cost / t.calls) : "—"}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Top users */}
                <Card className="border-[var(--ia-border)]">
                  <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Users className="h-4 w-4 text-[var(--ia-blue)]" />Top utilisateurs (par tokens)</CardTitle></CardHeader>
                  <CardContent className="p-0">
                    {usageData.top_users.length === 0
                      ? <p className="text-xs text-[var(--ia-muted)] py-8 text-center">Aucune donnée sur cette période</p>
                      : <Table data-testid="top-users-table">
                          <TableHeader>
                            <TableRow className="bg-slate-50">
                              <TableHead className="text-[10px]">#</TableHead>
                              <TableHead className="text-[10px]">Utilisateur</TableHead>
                              <TableHead className="text-[10px]">Rôle</TableHead>
                              <TableHead className="text-[10px] text-right">Entrée</TableHead>
                              <TableHead className="text-[10px] text-right">Sortie</TableHead>
                              <TableHead className="text-[10px] text-right">Total</TableHead>
                              <TableHead className="text-[10px] text-right">Appels</TableHead>
                              <TableHead className="text-[10px] text-right">Coût</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {usageData.top_users.map((tu, i) => (
                              <TableRow key={tu.user_id}>
                                <TableCell className="text-[10px] text-[var(--ia-muted)]">{i + 1}</TableCell>
                                <TableCell className="text-xs"><div className="leading-tight"><div>{tu.name}</div><div className="text-[10px] text-[var(--ia-muted)]">{tu.email}</div></div></TableCell>
                                <TableCell><Badge variant="outline" className="text-[9px]">{tu.role}</Badge></TableCell>
                                <TableCell className="text-[10px] text-right font-mono">{formatNum(tu.input_tokens)}</TableCell>
                                <TableCell className="text-[10px] text-right font-mono">{formatNum(tu.output_tokens)}</TableCell>
                                <TableCell className="text-[10px] text-right font-mono font-semibold">{formatNum(tu.total_tokens)}</TableCell>
                                <TableCell className="text-[10px] text-right">{tu.calls}</TableCell>
                                <TableCell className="text-[10px] text-right font-mono text-emerald-700">{formatEur(tu.cost_eur)}</TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                    }
                  </CardContent>
                </Card>
              </>
            )}
          </div>
        </TabsContent>

        {/* ─── Coupons ─── */}
        <TabsContent value="coupons" data-testid="admin-coupons-content">
          <div className="space-y-4">
            <Card className="border-[var(--ia-border)]">
              <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}><Tag className="h-4 w-4 text-emerald-600" />Créer un code promo</CardTitle></CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 sm:grid-cols-5 gap-3">
                  <div>
                    <Label className="text-xs">Code</Label>
                    <Input data-testid="coupon-code-input" placeholder="RGPD20" value={newCoupon.code} onChange={(e) => setNewCoupon(p => ({ ...p, code: e.target.value.toUpperCase() }))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Type</Label>
                    <Select value={newCoupon.discount_type} onValueChange={(v) => setNewCoupon(p => ({ ...p, discount_type: v }))}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="percent">Pourcentage (%)</SelectItem>
                        <SelectItem value="fixed">Fixe (€)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Valeur</Label>
                    <Input data-testid="coupon-value-input" type="number" min="1" step="0.5" value={newCoupon.discount_value} onChange={(e) => setNewCoupon(p => ({ ...p, discount_value: e.target.value }))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Expire le (optionnel)</Label>
                    <Input type="date" value={newCoupon.valid_until} onChange={(e) => setNewCoupon(p => ({ ...p, valid_until: e.target.value }))} className="h-8 text-xs" />
                  </div>
                  <div>
                    <Label className="text-xs">Max utilisations</Label>
                    <Input type="number" placeholder="∞" value={newCoupon.max_uses} onChange={(e) => setNewCoupon(p => ({ ...p, max_uses: e.target.value }))} className="h-8 text-xs" />
                  </div>
                </div>
                <div className="flex justify-end mt-3">
                  <Button onClick={createCoupon} disabled={savingCoupon} className="bg-emerald-600 hover:bg-emerald-700" data-testid="coupon-create-btn">
                    {savingCoupon ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />} Créer le coupon
                  </Button>
                </div>
              </CardContent>
            </Card>

            <Card className="border-[var(--ia-border)]">
              <CardHeader className="pb-2"><CardTitle className="text-sm">Codes promo ({coupons.length})</CardTitle></CardHeader>
              <CardContent className="p-0">
                {coupons.length === 0
                  ? <p className="text-xs text-[var(--ia-muted)] py-8 text-center">Aucun code promo créé</p>
                  : <Table data-testid="coupons-table">
                      <TableHeader>
                        <TableRow className="bg-slate-50">
                          <TableHead className="text-[10px]">Code</TableHead>
                          <TableHead className="text-[10px]">Réduction</TableHead>
                          <TableHead className="text-[10px]">Utilisations</TableHead>
                          <TableHead className="text-[10px]">Expire</TableHead>
                          <TableHead className="text-[10px]">Statut</TableHead>
                          <TableHead className="text-[10px]">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {coupons.map(c => (
                          <TableRow key={c.code} data-testid={`coupon-row-${c.code}`}>
                            <TableCell className="text-xs font-mono font-semibold">{c.code}</TableCell>
                            <TableCell className="text-xs">{c.discount_type === "percent" ? `${c.discount_value}%` : `${c.discount_value} €`}</TableCell>
                            <TableCell className="text-xs">{c.used_count || 0}{c.max_uses ? ` / ${c.max_uses}` : ""}</TableCell>
                            <TableCell className="text-[10px] text-[var(--ia-muted)]">{c.valid_until ? new Date(c.valid_until).toLocaleDateString("fr-FR") : "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className={c.active ? "bg-emerald-50 text-emerald-600 border-emerald-200 text-[9px]" : "bg-slate-100 text-slate-500 text-[9px]"}>
                                {c.active ? "Actif" : "Inactif"}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <div className="flex gap-1">
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => toggleCoupon(c.code)} title={c.active ? "Désactiver" : "Activer"}>
                                  {c.active ? <Pause className="h-3.5 w-3.5 text-amber-600" /> : <Play className="h-3.5 w-3.5 text-emerald-600" />}
                                </Button>
                                <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => deleteCoupon(c.code)} title="Supprimer">
                                  <Trash2 className="h-3.5 w-3.5 text-red-500" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                }
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ─── Audit Logs ─── */}
        <TabsContent value="audit" data-testid="admin-audit-content">
          <Card className="border-[var(--ia-border)]">
            <CardHeader className="pb-2 flex flex-row items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                <FileText className="h-4 w-4 text-blue-600" />Journal d'audit ({auditLogs.length})
              </CardTitle>
              <Button size="sm" variant="outline" onClick={loadAuditLogs}>Actualiser</Button>
            </CardHeader>
            <CardContent className="p-0">
              {auditLogs.length === 0
                ? <p className="text-xs text-[var(--ia-muted)] py-8 text-center">Aucun événement enregistré</p>
                : <Table data-testid="audit-logs-table">
                    <TableHeader>
                      <TableRow className="bg-slate-50">
                        <TableHead className="text-[10px]">Date</TableHead>
                        <TableHead className="text-[10px]">Acteur</TableHead>
                        <TableHead className="text-[10px]">Action</TableHead>
                        <TableHead className="text-[10px]">Détails</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {auditLogs.map((log, i) => (
                        <TableRow key={i}>
                          <TableCell className="text-[10px] text-[var(--ia-muted)]">{formatDate(log.created_at)}</TableCell>
                          <TableCell className="text-xs">{log.actor_email || "—"}<br /><span className="text-[10px] text-[var(--ia-muted)]">{log.actor_role}</span></TableCell>
                          <TableCell><Badge variant="outline" className="text-[9px] font-mono">{log.action}</Badge></TableCell>
                          <TableCell className="text-[10px] font-mono text-slate-600">{log.details && Object.keys(log.details).length ? JSON.stringify(log.details) : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
              }
            </CardContent>
          </Card>
        </TabsContent>

        {/* ─── Sécurité ─── */}
        <TabsContent value="security">
          <div className="space-y-6">
            {extended?.failed_logins?.length > 0 && (
              <Card className="border-red-200">
                <CardHeader className="pb-2"><CardTitle className="text-base text-red-600 flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}><ShieldAlert className="h-4 w-4" />Tentatives de connexion échouées (3+)</CardTitle></CardHeader>
                <CardContent><div className="space-y-2">
                  {extended.failed_logins.map((f, i) => (
                    <div key={i} className="flex items-center justify-between text-sm p-2 rounded-md bg-red-50 border border-red-100">
                      <span className="text-slate-700 font-mono text-xs">{f.identifier}</span>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-[9px] text-red-600 border-red-300">{f.count} tentatives</Badge>
                        <span className="text-[10px] text-[var(--ia-muted)]">{formatDate(f.last_attempt)}</span>
                      </div>
                    </div>
                  ))}
                </div></CardContent>
              </Card>
            )}
            {extended?.failed_logins?.length === 0 && (
              <Card className="border-green-200"><CardContent className="p-6 text-center text-sm text-green-700">
                <ShieldAlert className="h-6 w-6 mx-auto mb-2 text-green-500" />Aucune alerte de sécurité. Tout est normal.
              </CardContent></Card>
            )}
            {extended?.inactive_users?.length > 0 && (
              <Card className="border-[var(--ia-border)]">
                <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}><Clock className="h-4 w-4 text-amber-500" />Comptes inactifs (30+ jours) — {extended.inactive_users.length}</CardTitle></CardHeader>
                <CardContent><div className="space-y-2">
                  {extended.inactive_users.map((u, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <div><span className="text-slate-700">{u.name || u.email}</span> <Badge variant="outline" className="text-[9px] ml-1">{u.role}</Badge></div>
                      <span className="text-[10px] text-[var(--ia-muted)]">Dernière connexion : {u.last_login_at ? formatDate(u.last_login_at) : "Jamais"}</span>
                    </div>
                  ))}
                </div></CardContent>
              </Card>
            )}
          </div>
        </TabsContent>

        {/* ─── Config IA ─── */}
        <TabsContent value="llm" data-testid="admin-llm-tab">
          <Alert className="mb-4 border-blue-200 bg-blue-50">
            <Info className="h-4 w-4 text-blue-600" />
            <AlertDescription className="text-xs text-blue-700">
              Configurez un <strong>modèle IA distinct par tier</strong> pour optimiser les coûts. Les utilisateurs <strong>Free</strong> consommeront le modèle économique, les <strong>Premium</strong> un modèle haut de gamme. Les modèles <strong>OpenRouter</strong> utilisent automatiquement votre clé universelle (configurée en .env).
            </AlertDescription>
          </Alert>

          {/* OpenRouter live status */}
          <Card className="mb-4 border-[var(--ia-border)]" data-testid="openrouter-status">
            <CardContent className="p-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Zap className="h-4 w-4 text-amber-500" />
                <span className="font-semibold">OpenRouter</span>
                <Badge variant="outline" className="text-[10px]">{orModels.length} modèles disponibles</Badge>
                {orModelsFetchedAt && <span className="text-[10px] text-[var(--ia-muted)]">Synchro : {new Date(orModelsFetchedAt).toLocaleString("fr-FR")}</span>}
              </div>
              <Button size="sm" variant="outline" onClick={() => loadOrModels(true)} disabled={orModelsLoading} data-testid="openrouter-refresh-btn">
                {orModelsLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> : <Activity className="h-3.5 w-3.5 mr-1.5" />}
                Actualiser
              </Button>
            </CardContent>
          </Card>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {["free", "premium"].map(tier => {
              const tc = tierConfigs[tier];
              const tierCfg = (llmConfig && llmConfig[tier]) || {};
              const provider = tc.selectedLLM.split(":")[0];
              const modelId = tc.selectedLLM.split(":").slice(1).join(":");
              const isCustom = CUSTOM_PROVIDERS.includes(provider);
              const isOR = provider === "openrouter";
              // Resolve dynamic pricing for the selected OR model
              const orMeta = isOR ? orModels.find(m => m.id === modelId) : null;
              return (
                <Card key={tier} className={`border-2 ${tier === "premium" ? "border-amber-200" : "border-emerald-200"}`} data-testid={`llm-config-${tier}`}>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {tier === "free"
                        ? <><Users className="h-4 w-4 text-emerald-600" />Modèle pour utilisateurs <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200">Gratuit</Badge></>
                        : <><Zap className="h-4 w-4 text-amber-600" />Modèle pour utilisateurs <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200">Premium</Badge></>
                      }
                    </CardTitle>
                    <p className="text-xs text-[var(--ia-muted)] mt-1">
                      {tier === "free"
                        ? "Suggéré : google/gemini-2.5-flash via OpenRouter (~0.10/0.40 €/M)"
                        : "Suggéré : anthropic/claude-sonnet-4.5 via OpenRouter (qualité maximale)"
                      }
                    </p>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Select
                      value={tc.selectedLLM}
                      onValueChange={(v) => setTierConfigs(p => ({ ...p, [tier]: { ...p[tier], selectedLLM: v, apiKey: CUSTOM_PROVIDERS.includes(v.split(":")[0]) ? p[tier].apiKey : "" } }))}
                    >
                      <SelectTrigger data-testid={`llm-select-${tier}`}><SelectValue placeholder="Choisir un modèle" /></SelectTrigger>
                      <SelectContent className="max-h-[400px]">
                        <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)]">OpenRouter (clé universelle)</div>
                        {emergentProviders.map(p => <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>)}
                        <div className="px-2 py-1 text-xs font-semibold text-[var(--ia-muted)] mt-2 border-t pt-2">Providers directs (clé API requise)</div>
                        {customProviders.map(p => <SelectItem key={`${p.provider}:${p.model}`} value={`${p.provider}:${p.model}`}>{p.label}</SelectItem>)}
                      </SelectContent>
                    </Select>

                    {/* OpenRouter full catalog with search */}
                    {isOR && (
                      <details className="rounded-md border border-[var(--ia-border)] bg-slate-50">
                        <summary className="cursor-pointer px-3 py-2 text-xs font-medium flex items-center gap-2 hover:bg-slate-100">
                          <Search className="h-3.5 w-3.5" /> Parcourir les {orModels.length} modèles OpenRouter
                        </summary>
                        <div className="p-2 space-y-2 max-h-[300px] overflow-y-auto">
                          <Input
                            placeholder="Rechercher un modèle..."
                            value={orSearch}
                            onChange={(e) => setOrSearch(e.target.value.toLowerCase())}
                            className="h-7 text-xs"
                            data-testid={`or-search-${tier}`}
                          />
                          {orModels
                            .filter(m => !orSearch || m.id.toLowerCase().includes(orSearch) || (m.name || "").toLowerCase().includes(orSearch))
                            .slice(0, 50)
                            .map(m => (
                              <button
                                key={m.id}
                                onClick={() => setTierConfigs(p => ({ ...p, [tier]: { ...p[tier], selectedLLM: `openrouter:${m.id}`, apiKey: "" } }))}
                                className="w-full text-left p-2 rounded hover:bg-white border border-transparent hover:border-[var(--ia-border)] text-xs"
                              >
                                <div className="flex items-center justify-between gap-2">
                                  <span className="font-mono text-[10px] truncate">{m.id}</span>
                                  {m.is_free && <Badge variant="outline" className="text-[9px] bg-emerald-50 text-emerald-600 border-emerald-200">Gratuit</Badge>}
                                </div>
                                {!m.is_free && (
                                  <div className="text-[10px] text-[var(--ia-muted)]">
                                    Entrée: {m.input_eur_per_1m.toFixed(3)} €/M · Sortie: {m.output_eur_per_1m.toFixed(3)} €/M
                                  </div>
                                )}
                              </button>
                            ))
                          }
                        </div>
                      </details>
                    )}

                    {/* Pricing preview */}
                    {isOR && orMeta && (
                      <div className="text-xs p-2 rounded bg-slate-50 border border-[var(--ia-border)]" data-testid={`pricing-preview-${tier}`}>
                        <span className="text-[var(--ia-muted)]">Prix : </span>
                        <span className="font-mono">{orMeta.input_eur_per_1m.toFixed(3)} € / 1M tokens entrée</span>
                        <span className="text-[var(--ia-muted)]"> · </span>
                        <span className="font-mono">{orMeta.output_eur_per_1m.toFixed(3)} € / 1M tokens sortie</span>
                      </div>
                    )}

                    {/* Custom direct provider — API key */}
                    {isCustom && (
                      <div className="animate-fade-in">
                        <Label className="text-xs">Clé API {provider}</Label>
                        {tierCfg.has_api_key && !tc.apiKey && <p className="text-xs text-green-600 mt-0.5">Configurée ({tierCfg.api_key_preview})</p>}
                        <div className="flex items-center gap-2 mt-1">
                          <Key className="h-4 w-4 text-[var(--ia-muted)]" />
                          <Input
                            type="password"
                            placeholder={tierCfg.has_api_key ? "Garder la clé actuelle..." : "Votre clé API..."}
                            value={tc.apiKey}
                            onChange={(e) => setTierConfigs(p => ({ ...p, [tier]: { ...p[tier], apiKey: e.target.value } }))}
                            data-testid={`llm-apikey-${tier}`}
                          />
                        </div>
                      </div>
                    )}
                    <div className="flex justify-end pt-2">
                      <Button
                        onClick={() => saveLLMConfig(tier)}
                        disabled={tc.saving}
                        className={tier === "premium" ? "bg-amber-600 hover:bg-amber-700" : "bg-emerald-600 hover:bg-emerald-700"}
                        data-testid={`save-llm-${tier}`}
                      >
                        {tc.saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                        Sauvegarder {tier === "free" ? "Gratuit" : "Premium"}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* Modale de confirmation suspension */}
      <Dialog open={!!suspendTarget} onOpenChange={(open) => { if (!open) setSuspendTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{suspendTarget?.suspended ? "Réactiver ce compte ?" : "Suspendre ce compte ?"}</DialogTitle>
            <DialogDescription>
              {suspendTarget?.suspended
                ? <>Vous allez réactiver <strong>{suspendTarget?.name || suspendTarget?.email}</strong> ({suspendTarget?.email}). L'utilisateur pourra à nouveau se connecter.</>
                : <>Vous allez suspendre <strong>{suspendTarget?.name || suspendTarget?.email}</strong> ({suspendTarget?.email}). L'utilisateur ne pourra plus se connecter jusqu'à réactivation.</>
              }
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setSuspendTarget(null)} data-testid="cancel-suspend-btn">Annuler</Button>
            <Button
              variant={suspendTarget?.suspended ? "default" : "destructive"}
              onClick={() => toggleSuspend(suspendTarget?._id)}
              data-testid="confirm-suspend-btn"
            >
              {suspendTarget?.suspended
                ? <><Play className="h-4 w-4 mr-2" /> Réactiver</>
                : <><Pause className="h-4 w-4 mr-2" /> Suspendre</>
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modale de confirmation suppression */}
      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) setDeleteTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer cet utilisateur ?</DialogTitle>
            <DialogDescription>
              Vous allez supprimer définitivement <strong>{deleteTarget?.name || deleteTarget?.email}</strong> ({deleteTarget?.email}) et toutes ses données (conversations, diagnostics, configurations).
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Annuler</Button>
            <Button variant="destructive" onClick={() => deleteUser(deleteTarget?._id)} data-testid="confirm-delete-user-btn">
              <Trash2 className="h-4 w-4 mr-2" /> Supprimer définitivement
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
